import React, { useEffect, useState } from 'react';
import { Card, CardContent, Checkbox, Grid, Popover, TextField, Typography, makeStyles } from '@material-ui/core';
import { ArrowDropDown, ChevronRight } from '@material-ui/icons';
import { isEmpty as _isEmpty, get as _get } from 'lodash';
import Theme from '../../ui/Theme';
import { sortItemsBySelection } from '../../utils/Utils';
import ReactInfiniteScroll from '../ReactInfiniteScroll';

const useStyles = makeStyles(() => ({
  inputCard: {
    width: (props) => props.inputWidth,
    height: '38px',
    position: 'relative',
    cursor: 'pointer',
  },
  placeholder: {
    fontFamily: 'Montserrat',
    fontWeight: '500',
    fontSize: '14px',
    padding: '10px',
  },
  arrowIcon: {
    position: 'absolute',
    top: 10,
    right: 0,
  },
  cardContent: {
    borderRadius: '8px',
    height: '480px',
    paddingBottom: '10px',
    width: (props) => (props.hasChildren ? 'auto' : props.popoverWidth),
  },
  scrollableGrid: {
    height: '420px',
    maxHeight: '420px',
    overflowY: 'auto',
    overflowX: 'hidden',
    '&::-webkit-scrollbar': { width: '10px' },
    '&::-webkit-scrollbar-track': {
      boxShadow: 'inset 0 0 5px grey',
      borderRadius: '20px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'lightGrey',
      borderRadius: '100px',
    },
  },
  searchText: {
    width: '100%',
    height: '42px',
    background: '#FAFAFA',
    border: '1px solid #E2E2E2',
    padding: '8px',
    fontFamily: 'Montserrat',
    fontWeight: '500',
    fontSize: '14px',
    borderRadius: '8px',
    marginBottom: '10px',
  },
  clear: {
    fontWeight: '500',
    fontSize: '12px',
    color: '#475467',
    cursor: 'pointer',
  },
  popover: {
    zIndex: 1,
  },
}));

export default function HorizontalMultiSelect({
  list,
  searchPlaceholder,
  selectedList,
  setSelectedList,
  placeholderButtonLabel,
  inputWidth = '300px',
  popoverWidth = '17vw',
  inputBackgroudColor = false,
  listSelectName = 'Dealerships',
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  const [childrenViewList, setChildrenViewList] = useState({
    id: 0,
    children: [],
  });

  const [searchVal, setSearchVal] = useState('');
  const [filteredList, setFilteredList] = useState([]);

  useEffect(() => {
    setFilteredList(list || []);
  }, [list]);

  const hasChildren = !_isEmpty(_get(childrenViewList, 'children'));
  const classes = useStyles({ inputWidth, popoverWidth, hasChildren });

  const handleSearch = (e) => {
    setChildrenViewList({
      id: 0,
      children: [],
    });
    const input = e.target.value;
    setSearchVal(input);
    const filteredArrayTemp = input
      ? list.filter(
          (item) =>
            item.name.toLowerCase().includes(input.toLowerCase()) ||
            item.children.some((i) => i.name.toLowerCase().includes(input.toLowerCase())),
        )
      : list;
    setFilteredList(filteredArrayTemp);
  };

  const handleParentChange = (e, dealer) => {
    setChildrenViewList(dealer);
    if (e.target.checked) {
      const temp = { ...selectedList };
      temp[dealer.id] = dealer.children.map((i) => i.id) || [];
      setSelectedList(temp);
    } else {
      const temp = { ...selectedList };
      delete temp[dealer.id];
      setSelectedList(temp);
    }
  };

  const handleChildChange = (e, child) => {
    if (e.target.checked) {
      const temp = { ...selectedList };
      if (temp[childrenViewList.id]) {
        temp[childrenViewList.id].push(child.id);
        temp[childrenViewList.id] = [...new Set(temp[childrenViewList.id])];
      } else {
        temp[childrenViewList.id] = [child.id];
      }
      setSelectedList(temp);
    } else {
      const temp = { ...selectedList };
      if (temp[childrenViewList.id].length > 1) {
        const index = temp[childrenViewList.id].indexOf(child.id);
        if (index > -1) {
          temp[childrenViewList.id].splice(index, 1);
        }
      } else {
        delete temp[childrenViewList.id];
      }
      setSelectedList(temp);
    }
  };

  const handleSelectAll = () => {
    const temp = {};
    // eslint-disable-next-line no-return-assign
    list.forEach((dealer) => (temp[dealer.id] = dealer.children.map((i) => i.id)));
    setSelectedList(temp);
  };

  // Selected list should move up in the list
  const sortedList = sortItemsBySelection(selectedList, filteredList, 'id');

  // check if all is marked checked
  const isChecked = Object.keys(selectedList).length === sortedList.length;

  return (
    <>
      <Card
        className={classes.inputCard}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        style={{
          ...(inputBackgroudColor
            ? {
                backgroundColor: Theme.palette.secondary.main,
              }
            : {}),
        }}
      >
        <Grid container>
          <Grid item xs={10}>
            <Typography noWrap className={classes.placeholder}>
              {!_isEmpty(selectedList)
                ? `${
                    Object.keys(selectedList).length +
                    Object.keys(selectedList)
                      .map((i) => selectedList[i].length)
                      .reduce((a, b) => a + b, 0)
                  } ${listSelectName} selected`
                : placeholderButtonLabel}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <ArrowDropDown className={classes.arrowIcon} />
          </Grid>
        </Grid>
      </Card>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        className={classes.popover}
        onClose={() => setAnchorEl(null)}
      >
        <Card className={classes.cardContent}>
          <CardContent>
            <Grid container direction="row">
              <Grid item xs={12}>
                <TextField
                  id="searchId"
                  name="search"
                  required
                  autoComplete="off"
                  autoSave="off"
                  onChange={handleSearch}
                  placeholder={searchPlaceholder}
                  value={searchVal}
                  InputProps={{
                    disableUnderline: true,
                    className: classes.searchText,
                  }}
                  rows={1}
                />
              </Grid>
            </Grid>
            <Grid container direction="row">
              <Grid item xs={hasChildren ? 6 : 12}>
                <Grid
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                  }}
                >
                  <Checkbox
                    checked={isChecked}
                    name="checkedBox"
                    color="primary"
                    style={{
                      color: isChecked ? '#5925DC' : '#D0D5DD',
                      borderRadius: '8px',
                    }}
                    onClick={isChecked ? () => setSelectedList({}) : handleSelectAll}
                  />
                  <Grid item xs={8} style={{ cursor: 'pointer' }}>
                    <Typography
                      style={{
                        fontWeight: '600',
                        fontSize: '14px',
                        color: isChecked ? '#622FEE' : '#667085',
                      }}
                    >
                      All
                    </Typography>
                  </Grid>
                </Grid>
                <ReactInfiniteScroll
                  size={20}
                  data={sortedList}
                  height={380}
                  renderItem={(dealer) => {
                    const checked = selectedList[dealer.id]?.length === dealer.children.length;
                    const partialChecked =
                      !_isEmpty(selectedList[dealer.id]) && selectedList[dealer.id].length < dealer.children.length;
                    return (
                      <Grid
                        container
                        key={dealer.id}
                        alignContent="center"
                        alignItems="center"
                        style={{
                          ...(childrenViewList.id === dealer.id ? { backgroundColor: '#F4F3FF' } : {}),
                          borderRadius: '8px',
                        }}
                      >
                        <Grid item xs={2}>
                          <Checkbox
                            checked={checked}
                            onChange={(e) => handleParentChange(e, dealer)}
                            name="checkedBox"
                            color="primary"
                            indeterminate={partialChecked}
                            style={{
                              color: partialChecked || checked ? '#5925DC' : '#D0D5DD',
                              borderRadius: '8px',
                            }}
                          />
                        </Grid>

                        <Grid item xs={8} style={{ cursor: 'pointer' }} onClick={() => setChildrenViewList(dealer)}>
                          <Typography
                            noWrap
                            style={{
                              fontWeight: '600',
                              fontSize: '14px',
                              color: partialChecked || checked ? '#622FEE' : '#667085',
                            }}
                          >
                            {dealer.name}
                          </Typography>
                        </Grid>
                        {dealer.children.length ? (
                          <Grid item xs={2}>
                            <ChevronRight style={{ cursor: 'pointer' }} onClick={() => setChildrenViewList(dealer)} />
                          </Grid>
                        ) : null}
                      </Grid>
                    );
                  }}
                />
              </Grid>
              {hasChildren && (
                <Grid item xs={6} className={classes.scrollableGrid} style={{ width: '100%' }}>
                  <ReactInfiniteScroll
                    size={20}
                    data={childrenViewList.children}
                    height={380}
                    renderItem={(child) => {
                      const checked = (selectedList[childrenViewList.id] || []).includes(child.id);

                      return (
                        <Grid container key={child.id} alignContent="center" alignItems="center">
                          <Grid item xs={2}>
                            <Checkbox
                              checked={checked}
                              onChange={(e) => handleChildChange(e, child)}
                              name="checkedBox2"
                              color="primary"
                              style={{
                                color: checked ? '#5925DC' : '#D0D5DD',
                                borderRadius: '8px',
                              }}
                            />
                          </Grid>

                          <Grid item xs={10}>
                            <Typography
                              noWrap
                              style={{
                                fontWeight: '600',
                                fontSize: '14px',
                                color: checked ? '#622FEE' : '#667085',
                              }}
                            >
                              {child.name}
                            </Typography>
                          </Grid>
                        </Grid>
                      );
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Popover>
    </>
  );
}
